import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Modal } from "@/components/base/Modal";
import { type AttributeDataType, formatAttributeValue, validateAttributeValue } from "@/lib/products";
import * as productApi from "@/lib/api/products";
import { flattenWithDepth, type Category } from "@/lib/categories";

const inputCls = "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

interface AttributeDef {
  id: string;
  name: string;
  data_type: AttributeDataType;
  options: string[];
  required: boolean;
  position: number;
  units: string | null;
  category_id: string | null;
}

interface AttributeValue {
  id: string;
  attribute_id: string;
  value: string | null;
}

interface Props {
  productId: string;
  orgId: string;
}

const DATA_TYPES: { value: AttributeDataType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Yes / No" },
  { value: "date", label: "Date" },
  { value: "select", label: "Single choice" },
  { value: "multiselect", label: "Multiple choice" },
];

export default function AttributesTab({ productId, orgId }: Props) {
  const queryClient = useQueryClient();
  const [defOpen, setDefOpen] = useState(false);
  const [defSaving, setDefSaving] = useState(false);
  const [defError, setDefError] = useState("");
  const [defForm, setDefForm] = useState({ name: "", data_type: "text" as AttributeDataType, options: "", required: false, units: "", category_id: "" });
  const [editingValue, setEditingValue] = useState<{ attrId: string; valueId: string | null; current: string } | null>(null);
  const [valueSaving, setValueSaving] = useState(false);
  const [valueError, setValueError] = useState("");
  const [inputVal, setInputVal] = useState("");
  const [deletingAttr, setDeletingAttr] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["product-attributes", orgId, productId],
    enabled: !!orgId && !!productId,
    queryFn: async () => {
      const [defs, vals, cats] = await Promise.all([
        supabase.from("product_attributes").select("id, name, data_type, options, required, position, units, category_id").eq("org_id", orgId).order("position"),
        supabase.from("product_attribute_values").select("id, attribute_id, value").eq("org_id", orgId).eq("product_id", productId),
        supabase.from("categories").select("id, name, parent_id, status").eq("org_id", orgId).order("name"),
      ]);
      const valueMap = new Map<string, AttributeValue>();
      (vals.data ?? []).forEach((v) => valueMap.set(v.attribute_id, v));
      return { defs: (defs.data ?? []) as AttributeDef[], valueMap, categories: (cats.data ?? []) as Category[] };
    },
  });

  const handleAddDef = async (e: FormEvent) => {
    e.preventDefault();
    setDefError("");
    if (!defForm.name.trim()) { setDefError("Attribute name is required."); return; }
    setDefSaving(true);
    const opts = defForm.options ? defForm.options.split(",").map((s) => s.trim()).filter(Boolean) : [];
    const nextPos = (data?.defs.length ?? 0);
    try {
      await productApi.upsertAttribute({
        orgId,
        name: defForm.name.trim(),
        dataType: defForm.data_type,
        options: opts,
        required: defForm.required,
        units: defForm.units || null,
        categoryId: defForm.category_id || null,
        position: nextPos,
      });
    } catch (e) {
      setDefSaving(false);
      setDefError((e as Error).message);
      return;
    }
    setDefSaving(false);
    setDefOpen(false);
    setDefForm({ name: "", data_type: "text", options: "", required: false, units: "", category_id: "" });
    queryClient.invalidateQueries({ queryKey: ["product-attributes", orgId, productId] });
  };

  const handleSaveValue = async () => {
    if (!editingValue) return;
    setValueError("");
    const attr = data?.defs.find((d) => d.id === editingValue.attrId);
    if (!attr) return;
    const valErr = validateAttributeValue(attr.data_type, inputVal);
    if (valErr) { setValueError(valErr); return; }
    setValueSaving(true);
    const existing = data?.valueMap.get(editingValue.attrId);
    if (existing) {
      await supabase.from("product_attribute_values").update({ value: inputVal }).eq("id", existing.id);
    } else {
      await supabase.from("product_attribute_values").insert({ org_id: orgId, product_id: productId, attribute_id: editingValue.attrId, value: inputVal });
    }
    setValueSaving(false);
    setEditingValue(null);
    setInputVal("");
    queryClient.invalidateQueries({ queryKey: ["product-attributes", orgId, productId] });
  };

  const handleDeleteAttr = async (attrId: string) => {
    setDeletingAttr(attrId);
    await productApi.deleteAttribute(orgId, attrId);
    setDeletingAttr(null);
    queryClient.invalidateQueries({ queryKey: ["product-attributes", orgId, productId] });
  };

  const startEdit = (attrId: string) => {
    const existing = data?.valueMap.get(attrId);
    setEditingValue({ attrId, valueId: existing?.id ?? null, current: existing?.value ?? "" });
    setInputVal(existing?.value ?? "");
    setValueError("");
  };

  const renderValueInput = (attr: AttributeDef) => {
    if (attr.data_type === "boolean") {
      return (
        <select value={inputVal} onChange={(e) => setInputVal(e.target.value)} className={inputCls}>
          <option value="">— not set —</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }
    if (attr.data_type === "select" || attr.data_type === "multiselect") {
      return (
        <select value={inputVal} onChange={(e) => setInputVal(e.target.value)} className={inputCls}>
          <option value="">— not set —</option>
          {attr.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (attr.data_type === "date") {
      return <input type="date" value={inputVal} onChange={(e) => setInputVal(e.target.value)} className={inputCls} />;
    }
    if (attr.data_type === "number") {
      return <input type="number" step="any" value={inputVal} onChange={(e) => setInputVal(e.target.value)} className={inputCls} placeholder="0" />;
    }
    return <input type="text" value={inputVal} onChange={(e) => setInputVal(e.target.value)} className={inputCls} placeholder="Enter value..." />;
  };

  if (isLoading) {
    return <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-400">Loading attributes...</div>;
  }

  const defs = data?.defs ?? [];

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Product Attributes</h3>
            <p className="text-xs text-slate-400 mt-0.5">Structured product properties — Material, Fit, Waterproof, Capacity, etc. Used for marketplace listings.</p>
          </div>
          <button
            onClick={() => { setDefError(""); setDefOpen(true); }}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
          >
            <i className="ri-add-line" /> Add attribute
          </button>
        </div>

        {defs.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <i className="ri-list-check text-3xl text-slate-200 mb-3 block" />
            <p className="text-sm font-medium text-slate-600 mb-1">No attributes defined</p>
            <p className="text-xs text-slate-400 max-w-xs mx-auto">Attributes like Material, Sleeve Length, or Waterproof help buyers find your product and map to marketplace aspects (eBay, Amazon, BigCommerce).</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {defs.map((attr) => {
              const val = data?.valueMap.get(attr.id);
              const isEditing = editingValue?.attrId === attr.id;
              const displayVal = val?.value != null && val.value !== "" ? formatAttributeValue(attr.data_type, val.value) : null;
              return (
                <div key={attr.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-slate-800">{attr.name}</span>
                        <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{DATA_TYPES.find((d) => d.value === attr.data_type)?.label ?? attr.data_type}</span>
                        {attr.units && <span className="text-xs bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded">{attr.units}</span>}
                        {attr.required && <span className="text-xs bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded">Required</span>}
                      </div>
                      {isEditing ? (
                        <div className="space-y-2">
                          {renderValueInput(attr)}
                          {valueError && <p className="text-xs text-red-600">{valueError}</p>}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={handleSaveValue}
                              disabled={valueSaving}
                              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap"
                            >
                              {valueSaving ? "Saving..." : "Save"}
                            </button>
                            <button
                              onClick={() => { setEditingValue(null); setInputVal(""); }}
                              className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <span
                          onClick={() => startEdit(attr.id)}
                          className="text-xs text-slate-500 cursor-pointer hover:text-emerald-700 transition-colors"
                        >
                          {displayVal ?? <span className="text-slate-300 italic">Not set — click to add</span>}
                        </span>
                      )}
                    </div>
                    {!isEditing && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => startEdit(attr.id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                          <i className="ri-pencil-line text-xs" />
                        </button>
                        <button
                          onClick={() => handleDeleteAttr(attr.id)}
                          disabled={deletingAttr === attr.id}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500"
                        >
                          <i className="ri-delete-bin-line text-xs" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Attribute Definitions Manager */}
      <Modal
        open={defOpen}
        onClose={() => setDefOpen(false)}
        title="Add Attribute"
        footer={
          <>
            <button onClick={() => setDefOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleAddDef} disabled={defSaving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {defSaving ? "Saving..." : "Add Attribute"}
            </button>
          </>
        }
      >
        <form onSubmit={handleAddDef} className="space-y-4">
          {defError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{defError}</div>}
          <div>
            <label className={labelCls}>Attribute name *</label>
            <input required value={defForm.name} onChange={(e) => setDefForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="e.g. Material, Waterproof, Capacity" />
          </div>
          <div>
            <label className={labelCls}>Data type</label>
            <select value={defForm.data_type} onChange={(e) => setDefForm((f) => ({ ...f, data_type: e.target.value as AttributeDataType }))} className={inputCls}>
              {DATA_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>
          {(defForm.data_type === "select" || defForm.data_type === "multiselect") && (
            <div>
              <label className={labelCls}>Options (comma separated)</label>
              <input value={defForm.options} onChange={(e) => setDefForm((f) => ({ ...f, options: e.target.value }))} className={inputCls} placeholder="Cotton, Polyester, Nylon, Wool" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Units (optional)</label>
              <input value={defForm.units} onChange={(e) => setDefForm((f) => ({ ...f, units: e.target.value }))} className={inputCls} placeholder="e.g. cm, V, ml, kg" />
            </div>
            <div>
              <label className={labelCls}>Applies to category</label>
              <select value={defForm.category_id} onChange={(e) => setDefForm((f) => ({ ...f, category_id: e.target.value }))} className={inputCls}>
                <option value="">All categories</option>
                {flattenWithDepth((data?.categories ?? []).filter((c) => (c.status ?? "active") !== "archived")).map((c) => <option key={c.id} value={c.id}>{"\u00A0".repeat(c.depth * 2)}{c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="attr-required" checked={defForm.required} onChange={(e) => setDefForm((f) => ({ ...f, required: e.target.checked }))} className="w-4 h-4 rounded accent-emerald-600" />
            <label htmlFor="attr-required" className="text-sm text-slate-700 cursor-pointer">Required for all products</label>
          </div>
          <p className="text-xs text-slate-400">This attribute will be available across all products. Map it to eBay aspects, Amazon attributes, or BigCommerce metafields later in channel settings.</p>
        </form>
      </Modal>
    </div>
  );
}